// pages/CreateEvent.js
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useAuth } from '../services/auth';
import { FiMoreHorizontal, FiFileText, FiTrash2, FiEye } from 'react-icons/fi';
import '../styles/CreateEvent.scss';

// Components
import TabNavigation from '../components/create/TabNavigation';
import CoverImageSection from '../components/create/CoverImageSection';
import BasicSection from '../components/create/BasicSection';
import RSVPSection from '../components/create/RSVPSection';
import PassesSection from '../components/create/PassesSection';
import MoreSection from '../components/create/MoreSection';
import StyleSection from '../components/create/StyleSection';
import CreateBackdrop from '../components/create/CreateBackdrop';
import InvitationPreview, { InvitationPreviewSheet } from '../components/create/InvitationPreview';
import { buildPreviewEvent } from '../utils/previewEvent';
import { parseLocalDate, toLocalDateString } from '../shared/eventDateTime';
import { parseBackgroundMusic } from '../shared/backgroundMusic';
import { track, trackOnce } from '../services/track';
import { EV } from '../shared/analyticsEvents';

// Modals
import CoverImageModal from '../components/create/CoverImageModal';
import LocationSearchModal from '../components/create/LocationSearchModal';

// Services
import {
  createEvent,
  updateEvent,
  uploadEventCoverImage,
  updateEventCoverImage,
  getEventById,
} from '../services/eventService';
import { getCommunityById } from '../services/communityService';
import supabase from '../services/supabase';
import Icon from '../components/Icon';
import { firstMediaUrl } from '../utils/mediaUrl';
import { publishVerificationGate, verificationReasonText, emailVerificationReasonText } from '../shared/verificationGate';
import BirthdaySheet from '../components/BirthdaySheet';
import PhoneVerifySheet from '../components/create/PhoneVerifySheet';
import { convertAndUploadPdf, pdfCoverColumns } from '../services/pdfCoverService';
import { fileToBase64 } from '../utils/pdfCoverUtils';
import { takeCoverPick } from '../services/coverPickAhead';
import { deviceTimeZone } from '../shared/eventTime';
import { parseInvitationMusic } from '../shared/invitationMusic';
import {
  saveDraft,
  readDraft,
  clearDraft,
  toStored,
  fromStored,
} from '../services/invitationDraft';

// Default background images - stored in public../assets/images/ folder
const DEFAULT_BACKGROUNDS = [
  '/images/backgrounds/1.jpg',
  '/images/backgrounds/2.jpg',
  '/images/backgrounds/3.jpg',
  '/images/backgrounds/4.jpg',
  '/images/backgrounds/6.jpg',
  '/images/backgrounds/7.jpg',
  '/images/backgrounds/8.jpg',
];


const getRandomBackground = () => {
  return DEFAULT_BACKGROUNDS[Math.floor(Math.random() * DEFAULT_BACKGROUNDS.length)];
};

/** 'HH:MM' from a Date's local clock - the shape of event_time / end_time. */
const clockString = (d) =>
  d instanceof Date && !Number.isNaN(d.getTime())
    ? `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    : null;

/** A Date on `day` at the clock time in an 'HH:MM[:SS]' column value. */
const timeOnDay = (day, time) => {
  if (!day || typeof time !== 'string') return null;
  const [h, m] = time.split(':').map((n) => parseInt(n, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  const d = new Date(day);
  d.setHours(h, m, 0, 0);
  return d;
};

// The width at which the preview moves beside the form (CreateEvent.scss).
const DESKTOP_QUERY = '(min-width: 1025px)';

/** Whether the desktop layout - form plus live phone preview - is showing. */
function useIsDesktop() {
  const get = () => typeof window !== 'undefined' && !!window.matchMedia?.(DESKTOP_QUERY).matches;
  const [isDesktop, setIsDesktop] = useState(get);
  useEffect(() => {
    const mql = window.matchMedia?.(DESKTOP_QUERY);
    if (!mql) return undefined;
    const onChange = () => setIsDesktop(mql.matches);
    mql.addEventListener?.('change', onChange);
    return () => mql.removeEventListener?.('change', onChange);
  }, []);
  return isDesktop;
}

export default function CreateEvent() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Stable across auth refreshes (the user object is not): the edit loader
  // below depends on it, and reloading mid-edit would wipe unsaved changes.
  const userId = user?.id ?? null;
  const [searchParams] = useSearchParams();
  const { eventId } = useParams();
  
  // Edit mode detection
  const isEditMode = !!eventId || searchParams.get('mode') === 'edit';
  const editEventId = eventId || searchParams.get('eventId');
  const communityId = searchParams.get('communityId');
  
  const scrollRef = useRef(null);
  const defaultBackgroundRef = useRef(getRandomBackground());

  // Tab state
  const [activeTab, setActiveTab] = useState('basic');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  // The ⋯ menu in the header: Save as draft, and Delete when editing.
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  // The loaded invitation's status, so saving a draft normally publishes it.
  const [editStatus, setEditStatus] = useState(null);
  // The loaded invitation's slug, for opening it after an update.
  const [editSlug, setEditSlug] = useState(null);
  // Why this invitation cannot be deleted, or null; undefined while checking.
  const [deleteBlocker, setDeleteBlocker] = useState(undefined);
  const [isDeleting, setIsDeleting] = useState(false);

  // ========== BASIC INFO STATE ==========
  const [title, setTitle] = useState('');
  const [eventType, setEventType] = useState('');
  const [description, setDescription] = useState('');
  const [coverImage, setCoverImage] = useState(null);
  const [blurredBackground, setBlurredBackground] = useState(null);
  const [coverImageChanged, setCoverImageChanged] = useState(false);

  // Date/Time state
  const [startDate, setStartDate] = useState(null);
  const [startTime, setStartTime] = useState(null);
  // Which clock the date and time are on (shared/eventTime.js): the host's
  // own unless they pick another.
  const [eventTimezone, setEventTimezone] = useState(() => deviceTimeZone());
  // Invitation music: a YouTube song for the end of the invitation
  // (shared/invitationMusic.js). null when there is none.
  const [invitationMusic, setInvitationMusic] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [endTime, setEndTime] = useState(null);

  // Location state
  const [location, setLocation] = useState(null);
  const [locationDetails, setLocationDetails] = useState(null);

  // Photo Pool & Comments
  const [allowGuestUploads, setAllowGuestUploads] = useState(true);
  const [allowGuestComments, setAllowGuestComments] = useState(true);

  // Custom fields and polls
  const [customFields, setCustomFields] = useState([]);
  const [polls, setPolls] = useState([]);

  // Playlists.
  //
  // selectedMusicService is what the invitation page actually gates playback
  // on; without it a saved link is stored and never played. See MusicSection.
  const [playlist, setPlaylist] = useState('');
  const [youtubePlaylist, setYoutubePlaylist] = useState('');
  const [selectedMusicService, setSelectedMusicService] = useState(null);

  // ========== LOOK ==========
  //
  // Theme, font, text colour and effect. The invitation page has rendered all
  // of these for a while; this form never wrote them, so a web-made invitation
  // came out plain next to the same one made in the app.
  //
  // customTheme is a shape, not a column: { type: 'color', value } |
  // { type: 'video', id, videoCdnUrl, thumbnailCdnUrl, defaultColor } |
  // { type: 'image' }. It is flattened into columns at save.
  const [customTheme, setCustomTheme] = useState(null);
  const [selectedFont, setSelectedFont] = useState('default');
  // null means automatic — the colour is derived from the theme at render
  // time, so changing the theme later moves the text with it.
  const [textColor, setTextColor] = useState(null);
  const [effectId, setEffectId] = useState(null);
  const [effectLayer, setEffectLayer] = useState('above');
  // Looping background music (shared/backgroundMusic.js shape), or null.
  const [backgroundMusic, setBackgroundMusic] = useState(null);

  // ========== RSVP STATE ==========
  const [privacy, setPrivacy] = useState('public');
  const [rsvpDeadline, setRsvpDeadline] = useState(null);
  const [maxAttendees, setMaxAttendees] = useState(null);
  const [plusOnesAllowed, setPlusOnesAllowed] = useState(0);
  const [hideMaybeRsvp, setHideMaybeRsvp] = useState(false);
  const [enableWaitlist, setEnableWaitlist] = useState(false);
  const [hideLocationBeforeVerification, setHideLocationBeforeVerification] = useState(false);
  // New private and approval invitations start with the venue hidden until
  // guests RSVP (shared/eventAccess.js, locationAccess). Only until the host
  // touches the switch or a draft restores it, and never when editing.
  const hideLocationTouchedRef = useRef(false);
  const chooseHideLocation = (value) => {
    hideLocationTouchedRef.current = true;
    setHideLocationBeforeVerification(value);
  };
  useEffect(() => {
    if (isEditMode || hideLocationTouchedRef.current) return;
    setHideLocationBeforeVerification(privacy !== 'public');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [privacy, isEditMode]);
  const [emojiSet, setEmojiSet] = useState('basic');
  const [customEmojis, setCustomEmojis] = useState({ going: '✅', maybe: '🤔', notGoing: '❌' });
  const [guestListIds, setGuestListIds] = useState([]);
  const [showInExploreFeed, setShowInExploreFeed] = useState(false);

  // ========== PASSES STATE ==========
  const [passes, setPasses] = useState([]);
  // 'Free', not 'Free Event' — the label now matches the app's ALL_PASS_TYPES
  // so the same choice reads the same on both surfaces.
  const [globalPassType, setGlobalPassType] = useState({ id: 'free', label: 'Free' });
  // Where a contribution goes. Nothing passes through the platform, so this is
  // the whole of the payment setup for the only non-free type still offered.
  const [hostUpiId, setHostUpiId] = useState('');
  // GST, bank details and a contact person all belonged to selling tickets,
  // which this form no longer offers. `hasSales` stays: an invitation that
  // already sold something must not have its cost type edited out from under
  // the people who paid.
  const [hasSales, setHasSales] = useState(false);

  // ========== MORE STATE ==========
  const [additionalInfoFields, setAdditionalInfoFields] = useState([]);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [gamesEnabled, setGamesEnabled] = useState(false);
  const [createdEventId, setCreatedEventId] = useState(null);
  const [linkedCommunity, setLinkedCommunity] = useState(null);
  const [currentFinancialYear, setCurrentFinancialYear] = useState(null);

  // Modals
  const [showCoverImageModal, setShowCoverImageModal] = useState(false);
  const [showLocationModal, setShowLocationModal] = useState(false);
  // Full-screen preview, from the header - how a phone sees the preview.
  const [showPreviewSheet, setShowPreviewSheet] = useState(false);
  const isDesktop = useIsDesktop();
  // The host as the invitation's Hosts section shows them, for the preview.
  const [hostProfile, setHostProfile] = useState(null);

  // Validation errors
  const [errors, setErrors] = useState({});

  // ========== EFFECTS ==========
  //
  // No sign-in bounce here.
  //
  // This effect used to send every signed-out visitor to /login the instant
  // the page mounted, which made "create an invitation" — the product's whole
  // proposition — something you could only see after handing over an email.
  // The account is still needed to publish; the ask now happens at that step,
  // on work already done. See handleCreateEvent and services/invitationDraft.

  /**
   * True once a restored draft has been published on the other side of a
   * sign-in, so the auto-publish cannot fire twice.
   */
  const resumeHandledRef = useRef(false);
  const resumeRequested = searchParams.get('resume') === '1';

  /** Set when a draft came back without its cover, so the page can say so. */
  const [coverDropped, setCoverDropped] = useState(false);

  /**
   * The button the person had pressed when the sign-in interrupted them, once
   * a draft carrying it has been restored. Acted on below, after
   * handleCreateEvent exists.
   */
  const [pendingResumeAction, setPendingResumeAction] = useState(null);
  const [termsNudge, setTermsNudge] = useState(0);

  // ── Phone verification ──────────────────────────────────────────────────
  //
  // Nothing is asked on arrival any more.
  //
  // This used to raise a confirm() the moment the page loaded, before anyone
  // had typed a word — the most expensive possible moment to ask a first-time
  // host for a phone number and an OTP, and the moment it protects least. The
  // rule now lives in shared/verificationGate.js and is applied at publish,
  // where it can see what the invitation actually is.
  //
  // Read here rather than at publish so pressing the button is instant; the
  // count comes with it because the gate needs to know if this is their first.
  const [phoneVerified, setPhoneVerified] = useState(null);
  // The other half of the gate: a phone signup is asked for an email instead.
  const [emailVerified, setEmailVerified] = useState(null);
  // What the gate asked for: 'phone' | 'email'.
  const [verifyNeed, setVerifyNeed] = useState('phone');
  const [publishedCount, setPublishedCount] = useState(0);

  // Asked after a first invitation is published, never before — see
  // components/BirthdaySheet.js for why not at sign-up.
  const [askBirthday, setAskBirthday] = useState(false);
  const [publishedSlug, setPublishedSlug] = useState(null);
  const [profileHasBirthday, setProfileHasBirthday] = useState(true);

  // Set when the gate fires: which reason to show, and the fact that a
  // publish is waiting behind it.
  const [verifyReason, setVerifyReason] = useState(null);

  useEffect(() => {
    if (!user) { setPhoneVerified(null); return undefined; }
    let cancelled = false;

    (async () => {
      const [{ data: profile }, { count }] = await Promise.all([
        supabase
          .from('users')
          .select('id, username, first_name, last_name, profile_photo_cdn_url, avatar_config, phone_verified, email_verified, birthday')
          .eq('id', user.id)
          .maybeSingle(),
        supabase
          .from('events')
          .select('id', { count: 'exact', head: true })
          .eq('host_id', user.id)
          .eq('status', 'published'),
      ]);

      if (cancelled) return;
      setPhoneVerified(profile?.phone_verified === true);
      setEmailVerified(profile ? profile.email_verified === true : null);
      setProfileHasBirthday(!!profile?.birthday);
      if (profile) {
        const { phone_verified: _pv, birthday: _bd, ...publicProfile } = profile;
        setHostProfile(publicProfile);
      }
      setPublishedCount(count ?? 0);
    })();

    return () => { cancelled = true; };
  }, [user]);

  // ── Product events (services/track.js) ──────────────────────────────────
  useEffect(() => {
    track(EV.CREATE_OPENED, { mode: isEditMode ? 'edit' : 'new', signed_in: !!user });
    // Once per page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { if (!isEditMode && title.trim()) trackOnce('create:title', EV.TITLE_ENTERED); }, [title, isEditMode]);
  useEffect(() => { if (!isEditMode && eventType) track(EV.EVENT_TYPE_SELECTED, { event_type: eventType }); }, [eventType, isEditMode]);
  useEffect(() => { if (!isEditMode && startDate) trackOnce('create:date', EV.DATE_SET); }, [startDate, isEditMode]);
  useEffect(() => { if (!isEditMode && location) trackOnce('create:location', EV.LOCATION_SET); }, [location, isEditMode]);

  // ── The preview ─────────────────────────────────────────────────────────
  //
  // The form as an events row, for the real invitation page to render (see
  // components/create/InvitationPreview.js). Rebuilt on every edit.
  const previewRow = useMemo(() => buildPreviewEvent({
    title, eventType, description, coverImage,
    startDate, startTime, endDate, endTime, location,
    allowGuestUploads, allowGuestComments, customFields, polls,
    playlist, youtubePlaylist, selectedMusicService,
    customTheme, selectedFont, textColor, effectId, effectLayer, backgroundMusic,
    privacy, rsvpDeadline, maxAttendees, plusOnesAllowed, hideMaybeRsvp,
    enableWaitlist, hideLocationBeforeVerification, emojiSet, customEmojis,
    passes, globalPassType, hostUpiId, additionalInfoFields, gamesEnabled,
    linkedCommunity, financialYear: currentFinancialYear, eventTimezone, invitationMusic,
  }, hostProfile), [
    title, eventType, description, coverImage,
    startDate, startTime, endDate, endTime, location,
    allowGuestUploads, allowGuestComments, customFields, polls,
    playlist, youtubePlaylist, selectedMusicService,
    customTheme, selectedFont, textColor, effectId, effectLayer, backgroundMusic,
    privacy, rsvpDeadline, maxAttendees, plusOnesAllowed, hideMaybeRsvp,
    enableWaitlist, hideLocationBeforeVerification, emojiSet, customEmojis,
    passes, globalPassType, hostUpiId, additionalInfoFields, gamesEnabled,
    linkedCommunity, currentFinancialYear, hostProfile, eventTimezone, invitationMusic,
  ]);

  // The look a new invitation opens with (create mode only): a random
  // template cover, sometimes with a video theme, fetched ahead so it shows at
  // once (services/coverPickAhead.js) - or a bundled background when none is
  // ready. A draft being resumed brings its own, so none is taken for it.
  useEffect(() => {
    if (isEditMode) return;
    const look = resumeRequested ? null : takeCoverPick();
    if (look) {
      setCoverImage({
        // Saved as a template: already on the CDN, never uploaded.
        type: 'template',
        uri: look.template.webpUrl,
        cdnUrl: look.template.webpUrl,
        templateId: look.template.id,
        isAnimated: false,
      });
      setBlurredBackground(look.template.webpUrl);
      if (look.video) {
        setCustomTheme({
          type: 'video',
          id: look.video.id,
          themeName: look.video.themeName,
          videoCdnUrl: look.video.videoCdnUrl,
          thumbnailCdnUrl: look.video.thumbnailCdnUrl,
          defaultColor: look.video.defaultColor || '#1a1a2e',
        });
      }
      return;
    }
    const defaultBgPath = defaultBackgroundRef.current;
    setCoverImage({
      uri: defaultBgPath,
      type: 'default',
      isAnimated: false
    });
    // Set blurred background to same image
    setBlurredBackground(defaultBgPath);
  }, [isEditMode, resumeRequested]);

  // ── The event type from the link ────────────────────────────────────────
  //
  // The marketing page's chips ("Birthday", "Wedding"…) arrive as
  // /create-invitation?type=birthday-party. Preselecting saves the first step and,
  // more usefully, means the cover picker opens on templates that match.
  useEffect(() => {
    if (isEditMode) return;
    const typeFromLink = searchParams.get('type');
    if (typeFromLink) setEventType(typeFromLink);
  }, [isEditMode, searchParams]);

  // ── Draft: collect ──────────────────────────────────────────────────────
  //
  // Everything the form holds, in a shape that survives JSON. Dates go through
  // toStored because JSON.parse would otherwise hand back strings that the
  // save path calls .getHours() on.
  const collectDraft = useCallback(() => ({
    title,
    // A picked PDF is a File, which JSON cannot hold — see the note where the
    // draft is restored.
    pdfFileName: coverImage?.type === 'pdf' ? coverImage.fileName : null,
    eventType,
    description,
    coverImage,
    blurredBackground,
    coverImageChanged,
    startDate: toStored(startDate),
    startTime: toStored(startTime),
    eventTimezone,
    invitationMusic,
    endDate: toStored(endDate),
    endTime: toStored(endTime),
    location,
    locationDetails,
    allowGuestUploads,
    allowGuestComments,
    customFields,
    polls,
    playlist,
    youtubePlaylist,
    selectedMusicService,
    customTheme,
    selectedFont,
    textColor,
    effectId,
    effectLayer,
    backgroundMusic,
    privacy,
    rsvpDeadline: toStored(rsvpDeadline),
    maxAttendees,
    plusOnesAllowed,
    hideMaybeRsvp,
    enableWaitlist,
    hideLocationBeforeVerification,
    emojiSet,
    customEmojis,
    guestListIds,
    showInExploreFeed,
    passes,
    globalPassType,
    hostUpiId,
    additionalInfoFields,
    agreedToTerms,
    gamesEnabled,
    activeTab,
  }), [
    title, eventType, description, coverImage, blurredBackground, coverImageChanged,
    startDate, startTime, eventTimezone, invitationMusic, endDate, endTime, location, locationDetails,
    allowGuestUploads, allowGuestComments, customFields, polls, playlist, youtubePlaylist,
    selectedMusicService, customTheme, selectedFont, textColor, effectId, effectLayer, backgroundMusic,
    privacy, rsvpDeadline, maxAttendees, plusOnesAllowed, hideMaybeRsvp, enableWaitlist,
    hideLocationBeforeVerification, emojiSet, customEmojis, guestListIds, showInExploreFeed,
    passes, globalPassType, hostUpiId, additionalInfoFields, agreedToTerms, gamesEnabled, activeTab,
  ]);

  // ── Draft: restore ──────────────────────────────────────────────────────
  //
  // Runs once, and only when the URL asked for it (?resume=1, which /login
  // sends the person back to). Without that condition, an abandoned draft
  // would silently overwrite a blank form someone opened days later.
  const draftRestoredRef = useRef(false);

  useEffect(() => {
    if (isEditMode || !resumeRequested || draftRestoredRef.current) return;
    const draft = readDraft();
    if (!draft) { draftRestoredRef.current = true; return; }
    draftRestoredRef.current = true;

    setTitle(draft.title ?? '');
    setEventType(draft.eventType ?? '');
    setDescription(draft.description ?? '');
    // A PDF cover does not survive the round trip: `coverImage.file` is a
    // File object, and JSON.stringify turns it into `{}`. Rather than restore
    // a PDF cover with no PDF in it — which would look chosen and then save
    // nothing — the cover is dropped and the reason is shown.
    if (draft.coverImage?.type === 'pdf') {
      setCoverDropped(true);
    } else if (draft.coverImage) {
      setCoverImage(draft.coverImage);
      setBlurredBackground(draft.blurredBackground ?? draft.coverImage.uri ?? null);
      setCoverImageChanged(!!draft.coverImageChanged);
    }
    setStartDate(fromStored(draft.startDate));
    setStartTime(fromStored(draft.startTime));
    if (draft.eventTimezone) setEventTimezone(draft.eventTimezone);
    setInvitationMusic(parseInvitationMusic(draft.invitationMusic));
    setEndDate(fromStored(draft.endDate));
    setEndTime(fromStored(draft.endTime));
    setLocation(draft.location ?? null);
    setLocationDetails(draft.locationDetails ?? null);
    setAllowGuestUploads(draft.allowGuestUploads ?? true);
    setAllowGuestComments(draft.allowGuestComments ?? true);
    setCustomFields(draft.customFields ?? []);
    setPolls(draft.polls ?? []);
    setPlaylist(draft.playlist ?? '');
    setYoutubePlaylist(draft.youtubePlaylist ?? '');
    setSelectedMusicService(draft.selectedMusicService ?? null);
    setCustomTheme(draft.customTheme ?? null);
    setSelectedFont(draft.selectedFont ?? 'default');
    setTextColor(draft.textColor ?? null);
    setEffectId(draft.effectId ?? null);
    setEffectLayer(draft.effectLayer ?? 'above');
    setBackgroundMusic(parseBackgroundMusic(draft.backgroundMusic));
    setPrivacy(draft.privacy ?? 'public');
    setRsvpDeadline(fromStored(draft.rsvpDeadline));
    setMaxAttendees(draft.maxAttendees ?? null);
    setPlusOnesAllowed(draft.plusOnesAllowed ?? 0);
    setHideMaybeRsvp(!!draft.hideMaybeRsvp);
    setEnableWaitlist(!!draft.enableWaitlist);
    hideLocationTouchedRef.current = true;
    setHideLocationBeforeVerification(!!draft.hideLocationBeforeVerification);
    setEmojiSet(draft.emojiSet ?? 'basic');
    if (draft.customEmojis) setCustomEmojis(draft.customEmojis);
    setGuestListIds(draft.guestListIds ?? []);
    setShowInExploreFeed(!!draft.showInExploreFeed);
    setPasses(draft.passes ?? []);
    if (draft.globalPassType) setGlobalPassType(draft.globalPassType);
    setHostUpiId(draft.hostUpiId ?? '');
    setAdditionalInfoFields(draft.additionalInfoFields ?? []);
    setAgreedToTerms(!!draft.agreedToTerms);
    setGamesEnabled(!!draft.gamesEnabled);
    setActiveTab(draft.activeTab ?? 'basic');
    // The cover was too large for storage and did not come back; the page says
    // so rather than quietly substituting a stock image.
    if (draft.coverDropped) setCoverDropped(true);

    // Not a direct call: this is set in the same batch as every setState
    // above, so the effect that acts on it runs once React has applied them
    // all. Calling handleCreateEvent here would read the state this render
    // still has — an empty form.
    if (draft.pendingAction && !draft.coverDropped) setPendingResumeAction(draft.pendingAction);
  }, [isEditMode, resumeRequested]);


  // Load event data if in edit mode
  const loadEventForEdit = useCallback(async () => {
    // Wait for sign-in: before it resolves there is no id to compare, and the
    // ownership check below would send the host of their own invitation away.
    if (!userId) return;
    try {
      setIsLoading(true);
      const event = await getEventById(editEventId);
      
      if (!event) {
        throw new Error('Event not found');
      }

      // Check if user is the host
      if (event.host_id !== userId) {
        alert('You do not have permission to edit this event');
        navigate('/dashboard');
        return;
      }

      // Store financial year
      setCurrentFinancialYear(event.financial_year);
      setCreatedEventId(event.id);

      // Populate all fields from event data
      setTitle(event.title || '');
      setEditStatus(event.status ?? null);
      setEditSlug(event.shareable_slug ?? null);
      setEventType(event.event_type || '');
      setDescription(event.description || '');
      
      // ── The cover, on edit ────────────────────────────────────────────
      //
      // This handled two of the five kinds of cover an invitation can have.
      // A template or a YouTube cover matched neither branch, so opening one
      // for editing showed an empty picker and a blank background — and then
      // saving wrote no cover columns, quietly stripping the cover off an
      // invitation the host had only opened to fix a typo.
      //
      // Everything goes through firstMediaUrl, which drops the app's
      // bundled-asset tokens and the Metro dev-server URLs that some rows
      // still carry; neither loads in a browser.
      if (event.cover_image_type === 'gif') {
        const gifUri = firstMediaUrl(event.gif_cover_cdn_url, event.gif_cover_direct_url);
        if (gifUri) {
          setCoverImage({
            uri: gifUri,
            cdnUrl: event.gif_cover_cdn_url,
            b2Url: event.gif_cover_direct_url,
            isAnimated: true,
            type: 'gif',
          });
          // The still, not the animation, behind the form: a looping GIF as
          // the page background is not something anyone can fill a form on.
          setBlurredBackground(
            firstMediaUrl(event.static_gif_cdn_url, event.cover_image_cdn_url) || gifUri,
          );
        }
      } else if (event.cover_image_type === 'pdf') {
        // The pages are already converted and on the CDN, so this loads as a
        // finished cover with no `file` — which is what stops the save path
        // re-converting a PDF that has not changed.
        const pages = Array.isArray(event.pdf_page_images) ? event.pdf_page_images : [];
        const firstPage = firstMediaUrl(pages[0]?.cdn_url, pages[0]?.direct_url);
        if (firstPage) {
          setCoverImage({
            uri: firstPage,
            thumbnailUri: firstPage,
            pageImages: pages.map((p) => p.cdn_url),
            pageCount: pages.length,
            isAnimated: false,
            type: 'pdf',
          });
          setBlurredBackground(firstPage);
        }
      } else if (event.cover_image_type === 'youtube') {
        const thumb = firstMediaUrl(
          event.youtube_thumbnail_cdn_url,
          event.youtube_thumbnail_direct_url,
        );
        if (thumb) {
          setCoverImage({
            uri: thumb,
            thumbnailUrl: thumb,
            videoId: event.youtube_video_id,
            originalUrl: event.youtube_video_url,
            isAnimated: false,
            type: 'youtube',
          });
          setBlurredBackground(thumb);
        }
      } else {
        const imageUri = firstMediaUrl(event.cover_image_cdn_url, event.cover_image_direct_url);
        if (imageUri) {
          setCoverImage({
            uri: imageUri,
            cdnUrl: event.cover_image_cdn_url,
            b2Url: event.cover_image_direct_url,
            isAnimated: false,
            // Kept as 'template' where it is one, so saving does not push a
            // file that is already on the CDN back through the uploader.
            type: event.cover_image_type === 'template' ? 'template' : 'image',
          });
          setBlurredBackground(imageUri);
        }
      }


      // Dates and times.
      //
      // From event_date / event_time / end_date / end_time - the columns the
      // app writes and the invitation page reads. This read start_datetime and
      // end_datetime, which are not columns on `events` at all, so every
      // invitation opened for editing here came up with no date or time.
      // An event saved before zones existed takes the host's own: that is the
      // clock its times were typed on.
      setEventTimezone(event.timezone || deviceTimeZone());
      setInvitationMusic(parseInvitationMusic(event.invitation_music));
      const startDay = parseLocalDate(event.event_date);
      if (startDay) {
        setStartDate(startDay);
        const startClock = timeOnDay(startDay, event.event_time);
        if (startClock) setStartTime(startClock);
      }
      const endDay = parseLocalDate(event.end_date);
      if (endDay) setEndDate(endDay);
      const endClock = timeOnDay(endDay || startDay, event.end_time);
      if (endClock) setEndTime(endClock);

      // Location
      if (event.location_name) {
        setLocation({
          display_name: event.location_name,
          formatted_address: event.location_address,
          lat: event.location_lat,
          lng: event.location_lng,
          place_id: event.location_place_id,
        });
      }

      // Settings
      setAllowGuestUploads(event.allow_guest_uploads ?? true);
      setAllowGuestComments(event.allow_guest_comments ?? true);
      setCustomFields(event.custom_fields || []);
      setPolls(event.polls || []);
      // Was event.spotify_playlist, which is not a column — so opening a
      // Spotify invitation for edit always showed an empty box and saving
      // wiped the link.
      setPlaylist(event.playlist || '');
      setSelectedMusicService(event.selected_music_service || null);

      // Look
      if (event.theme_type === 'video' && event.theme_video_cdn_url) {
        setCustomTheme({
          type: 'video',
          id: event.theme_id,
          videoCdnUrl: event.theme_video_cdn_url,
          thumbnailCdnUrl: event.theme_thumbnail_cdn_url,
          defaultColor: event.custom_theme_color || '#1a1a2e',
        });
      } else if (event.theme_type === 'color' || event.custom_theme_color) {
        setCustomTheme({ type: 'color', value: event.custom_theme_color });
      } else if (event.theme_type === 'image') {
        setCustomTheme({ type: 'image' });
      }
      setSelectedFont(event.font_family || 'default');
      setTextColor(event.text_color || null);
      setEffectId(event.effect_id ?? null);
      setEffectLayer(event.effect_layer ?? 'above');
      setBackgroundMusic(parseBackgroundMusic(event.background_music));
      setGamesEnabled(event.games_enabled === true);
      setHostUpiId(event.host_upi_id || '');
      setYoutubePlaylist(event.youtube_playlist || '');

      // RSVP Settings
      setPrivacy(event.privacy || 'public');
      setRsvpDeadline(event.rsvp_deadline ? new Date(event.rsvp_deadline) : null);
      setMaxAttendees(event.max_attendees || null);
      setPlusOnesAllowed(event.plus_ones_allowed || 0);
      setHideMaybeRsvp(event.hide_maybe_rsvp ?? false);
      setEnableWaitlist(event.enable_waiting_list ?? false);
      setHideLocationBeforeVerification(event.hide_location_before_verification ?? false);
      setShowInExploreFeed(event.show_in_explore_feed ?? false);
      setEmojiSet(event.emoji_set || 'basic');
      
      if (event.custom_emojis) {
        setCustomEmojis(event.custom_emojis);
      }

      // Guest lists
      if (event.guest_list_ids) {
        setGuestListIds(event.guest_list_ids);
      }

      // Passes.
      //
      // From `pass_type`, which is the column that says what the invitation
      // is. This used to infer it — "any pass with a price above zero means
      // paid" — which cannot tell a contribution from a free invitation,
      // since a contribution's placeholder pass has a price of 0. So every
      // contribution opened for edit came back as Free, and saving turned it
      // into one.
      if (event.passes && event.passes.length > 0) setPasses(event.passes);

      const PASS_LABELS = {
        free: 'Free',
        contribution: 'Contribution',
        fixed: 'Fixed Amount',
        variable: 'Pay What You Can',
        paid: 'Paid Event',
      };
      const loadedType = event.pass_type || 'free';
      setGlobalPassType({ id: loadedType, label: PASS_LABELS[loadedType] ?? loadedType });

      // Bank details, GST and the contact person are not read any more: they
      // belonged to selling tickets, which this form no longer offers. The
      // columns are untouched on invitations that have them.


      // Has anyone paid for this invitation yet?
      //
      // Was `ticket_sales`, which is not a table in this database and never
      // has been — every edit logged a 404 and `hasSales` stayed false, so a
      // cost type could be changed out from under people who had already
      // paid. The table is `attendee_pass_purchases`, the same one the
      // invitation page reads a guest's own purchase from.
      const { data: salesData } = await supabase
        .from('attendee_pass_purchases')
        .select('id')
        .eq('event_id', editEventId)
        .limit(1);

      if (salesData && salesData.length > 0) {
        setHasSales(true);
      }

      // Additional info fields
      if (event.additional_info_fields) {
        setAdditionalInfoFields(event.additional_info_fields);
      }

      // Linked community
      if (event.community_id) {
        const community = await getCommunityById(event.community_id);
        if (community) {
          setLinkedCommunity(community);
        }
      }

    } catch (error) {
      console.error('Error loading event:', error);
      alert('Failed to load event data');
      navigate('/dashboard');
    } finally {
      setIsLoading(false);
    }
  }, [editEventId, navigate, userId]);

  useEffect(() => {
    if (isEditMode && editEventId) {
      loadEventForEdit();
    }
  }, [isEditMode, editEventId, loadEventForEdit]);

  // Load community if pre-selected
  useEffect(() => {
    if (communityId && !isEditMode) {
      loadCommunity(communityId);
    }
  }, [communityId, isEditMode]);

  // ========== LOAD COMMUNITY ==========
  const loadCommunity = async (id) => {
    try {
      const community = await getCommunityById(id);
      if (community) {
        setLinkedCommunity(community);
        console.log('✅ Community pre-selected:', community.name);
      }
    } catch (error) {
      console.error('Error loading community:', error);
    }
  };

  // ========== LOAD EVENT FOR EDIT ==========
  // ========== IMAGE HANDLERS ==========
  const handleImageSelect = async (image) => {
    track(EV.COVER_SELECTED, { type: image?.type });
    try {
      setCoverImage(image);
      setCoverImageChanged(true);
      setShowCoverImageModal(false);
      
      // Set blurred background
      setBlurredBackground(image.uri);
      
      console.log('✅ Image selected:', image.type);
    } catch (error) {
      console.error('Error selecting image:', error);
      alert('Failed to process image');
    }
  };

  const handleLocationSelect = (selectedLocation) => {
    setLocation(selectedLocation);
    setShowLocationModal(false);
  };

  // ========== VALIDATION ==========
  const validateForm = () => {
    const newErrors = {};

    // Basic validation
    if (!title.trim()) newErrors.title = 'Event title is required';
    if (!eventType) newErrors.eventType = 'Event type is required';
    if (!startDate) newErrors.startDate = 'Start date is required';
    if (!startTime) newErrors.startTime = 'Start time is required';

    // RSVP validation
    if (!rsvpDeadline) newErrors.rsvpDeadline = 'RSVP deadline is required';

    // Start datetime validation
    if (startDate && startTime) {
      const startDateTime = new Date(startDate);
      startDateTime.setHours(startTime.getHours(), startTime.getMinutes());
      
      if (startDateTime < new Date()) {
        newErrors.startDateTime = 'Event start time must be in the future';
      }

      // RSVP deadline must be before event
      if (rsvpDeadline && rsvpDeadline > startDateTime) {
        newErrors.rsvpDeadline = 'RSVP deadline must be before event starts';
      }
    }

    // A contribution needs somewhere for the money to go, and that is the
    // whole of it.
    //
    // This used to demand a bank account, an IFSC code, a GST number and a
    // contact person for "paid" and "donation" invitations. Those types are no
    // longer offered (see PassesSection — the app's TICKETED_PASSES flag is
    // off), and none of that applies to a contribution: guests pay the host
    // over UPI and nothing touches the platform.
    if (globalPassType?.id === 'contribution' && !hostUpiId.trim()) {
      newErrors.hostUpiId = 'Add the UPI ID guests should send their share to';
    }

    setErrors(newErrors);
    // The errors themselves, or null. Was a boolean, which told the caller
    // that something was wrong but not what or where.
    return Object.keys(newErrors).length > 0 ? newErrors : null;
  };

  // ========== TAB CHANGE ==========
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  };

  // ========== CREATE/UPDATE EVENT ==========
  const handleCreateEvent = async ({ asDraft = false } = {}) => {
    if (!asDraft) track(EV.PUBLISH_TAPPED, { mode: isEditMode ? 'edit' : 'new', signed_in: !!user });
    setShowMoreMenu(false);

    // ── The sign-in, at the last step ─────────────────────────────────────
    //
    // events.host_id is not nullable and the row-level policy checks it
    // against auth.uid(), so an anonymous insert cannot succeed — the account
    // is genuinely required, and the only question was when to ask. Asking
    // here means the person has already chosen a cover, named the thing and
    // picked a date; the draft goes to localStorage, /login brings them back
    // to ?resume=1, and the publish finishes itself.
    if (!user) {
      track(EV.PUBLISH_BLOCKED, { reason: 'sign_in' });
      const { ok, coverDropped: lostCover } = saveDraft({
        ...collectDraft(),
        // Which button they pressed, so the same one is pressed for them on
        // the way back — Save as draft must not turn into Publish.
        pendingAction: asDraft ? 'draft' : 'publish',
      });
      if (!ok) {
        // Storage refused (private mode, or a browser blocking it). Saying so
        // is the only honest option; sending them to /login would lose the
        // lot.
        alert(
          'Your browser will not let this page keep a draft, so signing in now '
          + 'would lose what you have filled in. Open this page in a normal window, '
          + 'or sign in first and start again.'
        );
        return;
      }
      if (lostCover) {
        alert('Your cover image is too large to hold while you sign in — you will need to pick it again after.');
      }
      // This page's own history entry becomes the resume address too, so the
      // browser's Back from /login reopens the draft instead of a blank form
      // (the draft only restores on ?resume=1). replaceState rather than a
      // router navigation: the router would re-run this page's restore now.
      try {
        window.history.replaceState(window.history.state, '', '/create-invitation?resume=1');
      } catch { /* the in-page Back on /login still returns them */ }
      navigate('/login', {
        state: { redirectTo: '/create-invitation?resume=1' },
      });
      return;
    }

    // The gate, at publish, where the invitation is finally known.
    //
    // Not on a draft: a draft is nobody's problem but the host's — it is not
    // sent, not listed, and not asking anyone for money. And never on an edit,
    // which would strand an invitation its host can no longer fix.
    if (!isEditMode && !asDraft && phoneVerified !== null) {
      // Asks for whichever of phone and email the account is missing - an
      // email or Google signup for its phone, a phone signup for its email.
      const { required, reason, need } = publishVerificationGate({
        phoneVerified,
        emailVerified: emailVerified === null ? undefined : emailVerified,
        passType: globalPassType?.id,
        privacy,
        showInExploreFeed,
      });

      if (required) {
        // Asked here, answered here. This used to be a confirm() offering to
        // navigate to Account Details — away from a finished invitation, to a
        // settings screen, with no route back to what they were doing. Most
        // people would not return. The sheet takes a number and a code, then
        // continues this same publish.
        setVerifyNeed(need || 'phone');
        track(EV.PUBLISH_BLOCKED, { reason: need === 'email' ? 'verification_email' : 'verification_phone', why: reason });
        setVerifyReason(reason);
        return;
      }
    }

    // A draft is for an unfinished invitation, so it skips the publish
    // checks. A title is the one thing needed, to find it again.
    if (asDraft) {
      if (!title.trim()) {
        alert('Give your draft a title so you can find it later.');
        setActiveTab('basic');
        return;
      }
    } else {
      const problems = validateForm();
      if (problems) {
        // Land on the tab the problem is actually on.
        //
        // This always jumped to Basic, so a missing RSVP deadline or UPI id
        // sent the host to a tab with nothing wrong on it and no indication of
        // where to look. The message now names the first problem too, instead
        // of "Please fix the errors" over a form with no visible error.
        const TAB_FOR = {
          title: 'basic', eventType: 'basic', startDate: 'basic',
          startTime: 'basic', startDateTime: 'basic',
          rsvpDeadline: 'rsvp',
          hostUpiId: 'passes', passes: 'passes',
        };
        const first = Object.keys(problems)[0];
        setActiveTab(TAB_FOR[first] ?? 'basic');
        track(EV.PUBLISH_BLOCKED, { reason: 'validation', field: first });
        alert(problems[first] || 'Please fix the errors before submitting');
        return;
      }
    }

    try {
      setIsSaving(true);

      // Combine date and time for start
      const combinedStartDateTime = startDate ? new Date(startDate) : null;
      if (combinedStartDateTime && startTime) {
        combinedStartDateTime.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);
      }

      // Combine date and time for end (if provided)
      let combinedEndDateTime = null;
      if (endDate && endTime) {
        combinedEndDateTime = new Date(endDate);
        combinedEndDateTime.setHours(endTime.getHours(), endTime.getMinutes(), 0, 0);
      } else if (endTime) {
        combinedEndDateTime = new Date(startDate);
        combinedEndDateTime.setHours(endTime.getHours(), endTime.getMinutes(), 0, 0);
      }

      // Prepare event data
      const eventData = {
        title: title.trim(),
        event_type: eventType,
        description: description.trim(),
        // Date and clock time as separate local values - the columns the app
        // writes and the invitation reads. This wrote start_datetime and
        // end_datetime, which are not columns on `events`, so PostgREST
        // rejected the whole insert or update.
        event_date: toLocalDateString(combinedStartDateTime),
        event_time: clockString(combinedStartDateTime),
        // Only when an end DATE was picked; an end time alone means the same
        // day, stored the way the app stores it.
        end_date: endDate ? toLocalDateString(endDate) : null,
        end_time: clockString(combinedEndDateTime),
        timezone: eventTimezone || null,
        invitation_music: invitationMusic || null,
        location_name: location?.display_name || null,
        location_address: location?.formatted_address || null,
        location_lat: location?.lat || null,
        location_lng: location?.lng || null,
        location_place_id: location?.place_id || null,
        allow_guest_uploads: allowGuestUploads,
        allow_guest_comments: allowGuestComments,
        custom_fields: customFields,
        polls: polls,
        // The column the invitation reads is `playlist`, not
        // `spotify_playlist` — which is not a column at all, so every Spotify
        // link saved from this site went into a field the database discarded.
        playlist: playlist || null,
        youtube_playlist: youtubePlaylist || null,
        // Without this the two above are stored and never played.
        selected_music_service: selectedMusicService || null,

        // A cover that is not uploaded still has to be recorded, or the
        // invitation has no cover at all.
        ...(coverImage?.type === 'template'
          ? {
              cover_image_type: 'template',
              cover_image_cdn_url: coverImage.uri,
              cover_image_direct_url: coverImage.uri,
            }
          : {}),
        ...(coverImage?.type === 'youtube'
          ? {
              cover_image_type: 'youtube',
              youtube_video_id: coverImage.videoId,
              youtube_video_url: coverImage.originalUrl,
              youtube_thumbnail_cdn_url: coverImage.thumbnailUrl,
            }
          : {}),

        // Look
        theme_type: customTheme?.type ?? null,
        theme_id: customTheme?.type === 'video' ? customTheme.id : null,
        theme_video_cdn_url: customTheme?.type === 'video' ? customTheme.videoCdnUrl : null,
        theme_thumbnail_cdn_url: customTheme?.type === 'video' ? customTheme.thumbnailCdnUrl : null,
        custom_theme_color: customTheme?.type === 'color'
          ? customTheme.value
          : (customTheme?.type === 'video' ? customTheme.defaultColor : null),
        font_family: selectedFont || 'default',
        text_color: textColor,
        effect_id: effectId,
        effect_layer: effectId ? effectLayer : null,
        // The track and the window of it that loops, or null.
        background_music: backgroundMusic,
        privacy: privacy,
        rsvp_deadline: rsvpDeadline ? rsvpDeadline.toISOString() : null,
        max_attendees: maxAttendees,
        plus_ones_allowed: plusOnesAllowed,
        hide_maybe_rsvp: hideMaybeRsvp,
        // enable_waiting_list is the column; enable_waitlist does not exist.
        enable_waiting_list: enableWaitlist,
        hide_location_before_verification: hideLocationBeforeVerification,
        show_in_explore_feed: showInExploreFeed,
        emoji_set: emojiSet,
        custom_emojis: emojiSet === 'custom' ? customEmojis : null,
        guest_list_ids: guestListIds.length > 0 ? guestListIds : null,
        passes: passes.length > 0 ? passes : null,
        pass_type: globalPassType?.id || 'free',
        // A contribution is paid host-to-guest over UPI, so this is the only
        // payment field either remaining pass type needs.
        host_upi_id: globalPassType?.id === 'contribution' ? (hostUpiId.trim() || null) : null,
        payment_method: globalPassType?.id === 'contribution' ? 'upi_direct' : null,
        games_enabled: gamesEnabled,
        additional_info_fields: additionalInfoFields.length > 0 ? additionalInfoFields : null,
        community_id: linkedCommunity?.id || null,
      };

      // Status is set explicitly. Left out, the column's default is 'draft',
      // so every invitation created here used to be saved as one.
      if (asDraft) {
        eventData.status = 'draft';
      } else if (!isEditMode || editStatus === 'draft') {
        eventData.status = 'published';
        eventData.published_at = new Date().toISOString();
      }

      let eventId;
      // The financial year the row actually landed in. createEvent works it
      // out server-side, so for a new invitation it is only knowable from what
      // comes back — and the cover update needs it.
      let eventFinancialYear = currentFinancialYear;
      // Where the invitation lives. /event/:slug and /i/:slug look it up in
      // event_slugs, which is keyed on the slug - opening them with the
      // event's UUID, as this page did, found nothing.
      let eventSlug = editSlug;

      if (isEditMode) {
        // Update existing event
        const updated = await updateEvent(editEventId, eventData);
        eventId = editEventId;
        eventSlug = updated?.shareable_slug ?? eventSlug;
        console.log('✅ Event updated:', eventId);
      } else {
        // Create new event
        const newEvent = await createEvent(eventData);
        eventId = newEvent.id;
        eventSlug = newEvent.shareable_slug ?? null;
        eventFinancialYear = newEvent.financial_year ?? currentFinancialYear;
        setCreatedEventId(eventId);
        setCurrentFinancialYear(eventFinancialYear);
        console.log('✅ Event created:', eventId);
      }

      // ── The cover ─────────────────────────────────────────────────────
      //
      // This block had two bugs, and between them no invitation ever created
      // on this website has had a cover image.
      //
      // First, the arguments were the wrong way round:
      // `uploadEventCoverImage(imageData, eventId)` was being called as
      // `(eventId, coverImage)`, so the function received a UUID string where
      // it expected the image and threw "Invalid image data: missing URI" on
      // its first line. The event had already been inserted by then, so what
      // the host saw was "Failed to save event" over an invitation that did
      // in fact exist — with no cover, and therefore no background either,
      // since the background falls back to the cover.
      //
      // Second, even had it run, its return value was dropped on the floor.
      // uploadEventCoverImage only puts bytes in storage and hands back the
      // URLs; writing them to the row is updateEventCoverImage's job, and
      // nothing was calling it.
      //
      // A template and a YouTube cover skip all of this: both are already
      // served from somewhere, and their columns go in with the event itself.
      const COVER_NEEDS_UPLOAD = !['default', 'template', 'youtube', 'pdf'].includes(
        coverImage?.type,
      );
      if ((coverImageChanged || !isEditMode) && coverImage?.uri && COVER_NEEDS_UPLOAD) {
        try {
          const uploadResult = await uploadEventCoverImage(coverImage, eventId);
          await updateEventCoverImage(eventId, eventFinancialYear, uploadResult);
        } catch (coverError) {
          // The invitation exists and everything else about it is saved. A
          // cover that would not upload is worth telling the host about, but
          // it is not worth throwing away the rest of their work over.
          console.error('Cover upload failed:', coverError);
          alert(
            'Your invitation was saved, but the cover image could not be uploaded. '
            + 'Open it for editing and try another image.'
          );
        }
      }

      // ── A PDF cover ───────────────────────────────────────────────────
      //
      // Converted here rather than when it was picked, because the converter
      // files pages under an event id and there was no event then. The same
      // order the app uses.
      //
      // This is slow — Ghostscript on a multi-page PDF, then an upload per
      // page — so it runs after the invitation row exists. If it fails, the
      // invitation is still saved and still has everything else; what it does
      // not have is a cover, and the host is told that rather than left to
      // discover it.
      if (coverImage?.type === 'pdf' && coverImage.file && (coverImageChanged || !isEditMode)) {
        try {
          const base64 = await fileToBase64(coverImage.file);
          const converted = await convertAndUploadPdf(
            { base64, fileName: coverImage.fileName || 'invitation.pdf' },
            eventId,
          );
          await updateEvent(eventId, pdfCoverColumns(converted));
        } catch (pdfError) {
          console.error('PDF conversion failed:', pdfError);
          alert(
            `Your invitation was saved, but the PDF could not be converted: ${pdfError.message} `
            + 'Open it for editing and try again, or pick a different cover.'
          );
        }
      }

      // No bank account to write.
      //
      // The block that used to be here upserted a row into `bank_accounts` and
      // linked it to the event, for pass types this form no longer offers. A
      // contribution is settled host-to-guest over UPI: `host_upi_id` and
      // `payment_method` on the event row are all of it.

      // It is a row now, so the local copy is no longer the only copy.
      clearDraft();

      if (asDraft) {
        track(EV.DRAFT_SAVED);
        alert('Saved as draft. Only you can see it until you publish.');
        navigate('/home');
        return;
      }
      track(EV.INVITATION_PUBLISHED, {
        mode: isEditMode ? 'edit' : 'new',
        event_type: eventType || null,
        privacy,
        pass_type: globalPassType?.id,
        cover_type: coverImage?.type,
      });

      // The one good moment to ask.
      //
      // They have just published an invitation, so they know what the product
      // does and the offer — "we will remind you to plan your own" — reads as
      // a feature. Only on a first invitation, and only if we do not already
      // know. Declining is a normal outcome and is not asked again here.
      if (!isEditMode && publishedCount === 0 && !profileHasBirthday) {
        setPublishedSlug(eventSlug);
        setAskBirthday(true);
        return;
      }

      alert(isEditMode ? 'Event updated successfully!' : 'Event created successfully!');
      navigate(eventSlug ? `/i/${eventSlug}` : '/home');

    } catch (error) {
      console.error('Error creating/updating event:', error);
      alert(error.message || 'Failed to save event. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * The publish handler, always the current one.
   *
   * handleCreateEvent closes over every field in the form. Wrapping it in a
   * useCallback would mean listing all forty of them — get one wrong and the
   * resume below publishes a stale version of the invitation. A ref updated on
   * each render sidesteps the question: the effect reads whatever the latest
   * render produced.
   */
  const handleCreateEventRef = useRef(handleCreateEvent);
  handleCreateEventRef.current = handleCreateEvent;

  /**
   * Press the button they were pressing when the sign-in interrupted.
   *
   * Waits on `phoneVerified` resolving: the publish path checks that value,
   * and a null means the answer has not come back yet — firing early would
   * race the phone prompt and put two dialogs on screen at once.
   */
  useEffect(() => {
    if (!pendingResumeAction || !user || resumeHandledRef.current) return;
    if (phoneVerified === null) return;
    resumeHandledRef.current = true;
    const action = pendingResumeAction;
    setPendingResumeAction(null);
    handleCreateEventRef.current({ asDraft: action === 'draft' });
  }, [pendingResumeAction, user, phoneVerified]);

  // ========== DELETE ==========
  // Asked when the menu opens, so the option shows enabled, or disabled with
  // the reason. Deletion is allowed only for the host, and only while no one
  // else has RSVPd or bought a ticket; the database re-checks on delete.
  useEffect(() => {
    if (!showMoreMenu || !isEditMode || !editEventId) return undefined;
    let cancelled = false;
    setDeleteBlocker(undefined);
    supabase.rpc('invitation_delete_blocker', { p_event_id: editEventId })
      .then(({ data, error }) => {
        if (cancelled) return;
        setDeleteBlocker(error ? 'Could not check right now. Try again later.' : (data ?? null));
      });
    return () => { cancelled = true; };
  }, [showMoreMenu, isEditMode, editEventId]);

  const handleDeleteEvent = async () => {
    if (!window.confirm(`Delete "${title || 'this invitation'}" for good, with its link? This cannot be undone.`)) return;
    setIsDeleting(true);
    const { error } = await supabase.rpc('delete_invitation', { p_event_id: editEventId });
    setIsDeleting(false);
    if (error) {
      alert(error.message || 'Could not delete this invitation.');
      return;
    }
    setShowMoreMenu(false);
    navigate('/home');
  };

  const alreadyPublished = isEditMode && editStatus !== 'draft';

  // ========== RENDER ==========
  if (isLoading) {
    return (
      <div className="create-event-page">
        <div className="create-event-loading">
          <div className="spinner"></div>
          <p>Loading invitation…</p>
        </div>
      </div>
    );
  }

  // Publish clicked with the terms unticked: MoreSection scrolls to the box
  // and shakes it, once per click.
  const nudgeTerms = () => {
    track(EV.PUBLISH_BLOCKED, { reason: 'terms' });
    setTermsNudge((n) => n + 1);
  };

  // The section tabs and the look controls. On a phone they sit in the form
  // (tabs across the top, look controls under the cover); on desktop they
  // move out to rails on the left and right edges of the window, so the
  // middle keeps its width for the preview and the fields and nothing pushes
  // the fields further down the page.
  const sectionTabs = (
    <TabNavigation
      activeTab={activeTab}
      onTabChange={handleTabChange}
      tabs={[
        { id: 'basic', label: 'Basic', icon: 'create' },
        { id: 'rsvp', label: 'RSVP', icon: 'people' },
        { id: 'passes', label: 'Passes', icon: 'ticket' },
        { id: 'more', label: 'More', icon: 'ellipsis-horizontal' },
      ]}
    />
  );
  const lookControls = (
    <StyleSection
      theme={customTheme}
      setTheme={setCustomTheme}
      font={selectedFont}
      setFont={setSelectedFont}
      textColor={textColor}
      setTextColor={setTextColor}
      effectId={effectId}
      setEffectId={setEffectId}
      effectLayer={effectLayer}
      setEffectLayer={setEffectLayer}
      backgroundMusic={backgroundMusic}
      setBackgroundMusic={setBackgroundMusic}
      invitationMusic={invitationMusic}
      setInvitationMusic={setInvitationMusic}
      eventType={eventType}
      coverImageUri={coverImage?.uri}
    />
  );

  return (
    <div className="create-event-page">
      {/* The ground: the theme if there is one, the blurred cover otherwise,
          and the chosen effect over the top — the same order the invitation
          itself uses, so what is on screen while editing is what guests get.
          `background-attachment: fixed` used to be set here, which is what
          made the embedded map flicker black on scroll: it forces the page
          onto one composited layer and every iframe on it repaints with it.
          CreateBackdrop uses a fixed child instead. */}
      {/* The effect plays behind the form on a phone, where the form is the
          only view of the invitation. On desktop it plays in the preview
          instead - once, where it belongs, rather than a second copy across
          the whole window behind the fields. */}
      <CreateBackdrop
        theme={customTheme}
        coverUri={blurredBackground}
        effectId={isDesktop ? null : effectId}
        effectLayer={effectLayer}
      />

      {/* Header */}
      <div className="create-event-header">
        {/* navigate(-1) alone dead-ends when this page IS the entry — which
            it now often is, since the marketing page links straight here and
            the tab bar opens it without a stack behind it. */}
        <button
          className="back-button"
          onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/home'))}
          aria-label="Back"
        >
          <Icon name="arrow-back" />
        </button>
        <h1>{isEditMode ? 'Edit invitation' : 'Create invitation'}</h1>
        {/* Phone and tablet only: on desktop the preview is already beside
            the form. */}
        <button
          type="button"
          className="back-button create-preview-button"
          onClick={() => { track(EV.PREVIEW_OPENED); setShowPreviewSheet(true); }}
          aria-label="Preview invitation"
        >
          <FiEye size={20} />
        </button>
        <div className="create-more">
          <button
            type="button"
            className="back-button"
            onClick={() => setShowMoreMenu((v) => !v)}
            aria-label="More options"
            aria-expanded={showMoreMenu}
          >
            <FiMoreHorizontal size={20} />
          </button>
          {showMoreMenu && (
            <>
              <div className="create-more-backdrop" onClick={() => setShowMoreMenu(false)} />
              <div className="create-more-menu" role="menu">
                {/* Disabled once published: turning a live invitation back
                    into a draft would take it offline for every guest. */}
                <button
                  type="button"
                  role="menuitem"
                  className="create-more-item"
                  onClick={() => handleCreateEvent({ asDraft: true })}
                  disabled={isSaving || alreadyPublished}
                >
                  <FiFileText size={16} />
                  <span>Save as draft</span>
                  {alreadyPublished && <small>Already published</small>}
                </button>
                {isEditMode && (
                  <button
                    type="button"
                    role="menuitem"
                    className="create-more-item is-danger"
                    onClick={handleDeleteEvent}
                    disabled={deleteBlocker !== null || isDeleting}
                  >
                    <FiTrash2 size={16} />
                    <span>{isDeleting ? 'Deleting…' : 'Delete invitation'}</span>
                    {deleteBlocker === undefined && <small>Checking…</small>}
                    {deleteBlocker && <small>{deleteBlocker}</small>}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="create-event-body">
        {isDesktop && (
          <nav className="create-rail create-rail--sections" aria-label="Invitation sections">
            {sectionTabs}
          </nav>
        )}

        <div className="create-main">
        {/* Live preview, desktop only:the published invitation page itself,
            in a phone-sized frame, following every edit - cover, theme, font,
            text colour, effect and the bottom RSVP bar exactly as guests will
            get them. It used to be a summary card that looked like none of
            that. Mounted only on desktop, so a phone does not load a second
            copy of the page it never shows. */}
        {isDesktop && (
          <aside className="create-preview">
            <div className="create-preview-phone">
              <InvitationPreview row={previewRow} />
            </div>
            <p className="create-preview-note">Live preview — this is how guests will see it.</p>
          </aside>
        )}

        <div className="create-form">
      {/* Tab Navigation: across the top on a phone, the left rail on desktop. */}
      {!isDesktop && sectionTabs}

      {/* Content */}
      <div className="create-event-content" ref={scrollRef}>
        <div className="content-container">
          {/* Says the quiet part out loud, once, at the top.
              Someone who arrived from the marketing page has no idea whether
              this is going to demand an account halfway through; telling them
              up front is cheaper than letting them wonder. */}
          {!user && !isEditMode && (
            <p className="create-note">
              No account needed to start — we will ask you to sign in when you publish,
              and bring you straight back here.
            </p>
          )}

          {coverDropped && (
            <p className="create-note create-note--warn">
              Your cover image was too large to keep while you signed in. Everything else
              is as you left it — pick a cover again when you are ready.
            </p>
          )}
          {activeTab === 'basic' && (
            <>
              <CoverImageSection
                coverImage={coverImage}
                onPress={() => setShowCoverImageModal(true)}
              />

              {/* Theme, font, text colour, effect — the app puts these
                  immediately under the cover, because they are all choices
                  about how the invitation looks rather than what it says.
                  On desktop they are in the right-hand rail instead. */}
              {!isDesktop && lookControls}

              
              <BasicSection
                title={title}
                setTitle={setTitle}
                eventType={eventType}
                setEventType={setEventType}
                description={description}
                setDescription={setDescription}
                startDate={startDate}
                setStartDate={setStartDate}
                startTime={startTime}
                setStartTime={setStartTime}
                eventTimezone={eventTimezone}
                setEventTimezone={setEventTimezone}
                endDate={endDate}
                setEndDate={setEndDate}
                endTime={endTime}
                setEndTime={setEndTime}
                location={location}
                onLocationPress={() => setShowLocationModal(true)}
                allowGuestUploads={allowGuestUploads}
                setAllowGuestUploads={setAllowGuestUploads}
                allowGuestComments={allowGuestComments}
                setAllowGuestComments={setAllowGuestComments}
                customFields={customFields}
                setCustomFields={setCustomFields}
                polls={polls}
                setPolls={setPolls}
                playlist={playlist}
                setPlaylist={setPlaylist}
                youtubePlaylist={youtubePlaylist}
                setYoutubePlaylist={setYoutubePlaylist}
                selectedMusicService={selectedMusicService}
                setSelectedMusicService={setSelectedMusicService}
                errors={errors}
              />
            </>
          )}

          {activeTab === 'rsvp' && (
            <RSVPSection
              privacy={privacy}
              setPrivacy={setPrivacy}
              rsvpDeadline={rsvpDeadline}
              setRsvpDeadline={setRsvpDeadline}
              maxAttendees={maxAttendees}
              setMaxAttendees={setMaxAttendees}
              plusOnesAllowed={plusOnesAllowed}
              setPlusOnesAllowed={setPlusOnesAllowed}
              hideMaybeRsvp={hideMaybeRsvp}
              setHideMaybeRsvp={setHideMaybeRsvp}
              enableWaitlist={enableWaitlist}
              setEnableWaitlist={setEnableWaitlist}
              hideLocationBeforeVerification={hideLocationBeforeVerification}
              setHideLocationBeforeVerification={chooseHideLocation}
              emojiSet={emojiSet}
              setEmojiSet={setEmojiSet}
              customEmojis={customEmojis}
              setCustomEmojis={setCustomEmojis}
              guestListIds={guestListIds}
              setGuestListIds={setGuestListIds}
              showInExploreFeed={showInExploreFeed}
              setShowInExploreFeed={setShowInExploreFeed}
              errors={errors}
            />
          )}

          {activeTab === 'passes' && (
            <PassesSection
              passes={passes}
              setPasses={setPasses}
              globalPassType={globalPassType}
              setGlobalPassType={setGlobalPassType}
              hostUpiId={hostUpiId}
              setHostUpiId={setHostUpiId}
              isEditMode={isEditMode}
              hasSales={hasSales}
            />
          )}

          {activeTab === 'more' && (
            <MoreSection
              additionalInfoFields={additionalInfoFields}
              setAdditionalInfoFields={setAdditionalInfoFields}
              eventId={createdEventId}
              financialYear={currentFinancialYear}
              linkedCommunity={linkedCommunity}
              setLinkedCommunity={setLinkedCommunity}
              agreedToTerms={agreedToTerms}
              setAgreedToTerms={setAgreedToTerms}
              termsNudge={termsNudge}
              gamesEnabled={gamesEnabled}
              setGamesEnabled={setGamesEnabled}
            />
          )}
        </div>
      </div>

      {/* Bottom Action Bar */}
      <div className="bottom-action-bar">
        <div className="action-bar-content">
          {activeTab === 'basic' && (
            <button
              className="btn btn-primary btn-next"
              onClick={() => handleTabChange('rsvp')}
            >
              <span>Next: RSVP</span>
              <Icon name="arrow-forward" />
            </button>
          )}

          {activeTab === 'rsvp' && (
            <div className="button-group">
              <button
                className="btn btn-secondary"
                onClick={() => handleTabChange('basic')}
              >
                <Icon name="arrow-back" />
                <span>Back</span>
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleTabChange('passes')}
              >
                <span>Next: Passes</span>
                <Icon name="arrow-forward" />
              </button>
            </div>
          )}

          {activeTab === 'passes' && (
            <div className="button-group">
              <button
                className="btn btn-secondary"
                onClick={() => handleTabChange('rsvp')}
              >
                <Icon name="arrow-back" />
                <span>Back</span>
              </button>
              <button
                className="btn btn-primary"
                onClick={() => handleTabChange('more')}
              >
                <span>Next: More</span>
                <Icon name="arrow-forward" />
              </button>
            </div>
          )}

          {activeTab === 'more' && (
            <div className="button-group">
              <button
                className="btn btn-secondary"
                onClick={() => handleTabChange('passes')}
              >
                <Icon name="arrow-back" />
                <span>Back</span>
              </button>
              <button
                className={`btn btn-success ${!agreedToTerms || isSaving ? 'disabled' : ''}`}
                // Greyed out until the terms are ticked, but not dead: a click
                // then scrolls to the box and shakes it, instead of doing
                // nothing and leaving the host to guess why.
                onClick={agreedToTerms ? handleCreateEvent : nudgeTerms}
                disabled={isSaving}
                aria-disabled={!agreedToTerms || isSaving}
                title={agreedToTerms ? undefined : 'Agree to the terms first'}
              >
                {isSaving ? (
                  <>
                    <div className="spinner-small"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Icon name="checkmark-circle" />
                    <span>
                      {isEditMode
                        ? 'Update invitation'
                        : user
                          ? 'Publish invitation'
                          : 'Sign in & publish'}
                    </span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
        </div>
        </div>{/* create-main */}

        {isDesktop && (
          <aside className="create-rail create-rail--look" aria-label="Look">
            {lookControls}
          </aside>
        )}
      </div>

      {verifyReason && (
        <PhoneVerifySheet
          kind={verifyNeed}
          reasonText={verifyNeed === 'email'
            ? emailVerificationReasonText(verifyReason)
            : verificationReasonText(verifyReason)}
          onVerified={() => {
            // Verified, so the gate passes on the way through. Straight back
            // into the publish the host already asked for — their last action
            // is still the button they pressed.
            if (verifyNeed === 'email') setEmailVerified(true);
            else setPhoneVerified(true);
            setVerifyReason(null);
            handleCreateEventRef.current({ asDraft: false });
          }}
          onCancel={() => setVerifyReason(null)}
        />
      )}

      {askBirthday && (
        <BirthdaySheet
          onSave={async (birthday) => {
            // Failing to save a birthday must not strand someone on a sheet
            // over an invitation that published perfectly well.
            const { error } = await supabase
              .from('users')
              .update({ birthday })
              .eq('id', user.id);
            if (error) throw new Error('Could not save that. You can add it in Settings.');
            setAskBirthday(false);
            navigate(publishedSlug ? `/i/${publishedSlug}` : '/home');
          }}
          onDismiss={() => {
            setAskBirthday(false);
            navigate(publishedSlug ? `/i/${publishedSlug}` : '/home');
          }}
        />
      )}

      {/* Modals */}
      {showCoverImageModal && (
        <CoverImageModal
          onClose={() => setShowCoverImageModal(false)}
          onSelectImage={handleImageSelect}
          eventType={eventType}
          context="event"
        />
      )}

      {showPreviewSheet && (
        <InvitationPreviewSheet row={previewRow} onClose={() => setShowPreviewSheet(false)} />
      )}

      {showLocationModal && (
        <LocationSearchModal
          onClose={() => setShowLocationModal(false)}
          onSelectLocation={handleLocationSelect}
        />
      )}
    </div>
  );
}